import type { CSSProperties, ReactNode } from 'react';
import { classes } from '../outils/classes';
import styles from './Graphiques.module.css';

export type TonReglette = 'bleu' | 'vert' | 'jaune' | 'rouge';

export interface ProprietesReglette {
  libelle: string;
  /** null : « non renseigné » est écrit, jamais remplacé par zéro. */
  valeur: number | null;
  /** Échelle commune à toutes les réglettes d'un même groupe. */
  max: number;
  /** Valeur affichée (par défaut, la valeur formatée en français). */
  affichage?: string;
  ton?: TonReglette;
  /** Variante « repère » : trait vertical de prévision. */
  repere?: number | null;
  libelleAbsent?: string;
}

export function formaterNombre(n: number): string {
  return n.toLocaleString('fr-FR');
}

export function Reglette({ libelle, valeur, max, affichage, ton = 'bleu', repere, libelleAbsent = 'non renseigné' }: ProprietesReglette) {
  const part = valeur === null || max <= 0 ? 0 : Math.max(0, Math.min(100, (valeur / max) * 100));
  const partRepere = repere === null || repere === undefined || max <= 0 ? null : Math.max(0, Math.min(100, (repere / max) * 100));
  return (
    <div className={styles.reglette}>
      <div className={styles.entete}>
        <span>{libelle}</span>
        {valeur === null ? <b className={styles.na}>{libelleAbsent}</b> : <b>{affichage ?? formaterNombre(valeur)}</b>}
      </div>
      <div
        className={styles.barre}
        role="img"
        aria-label={
          valeur === null
            ? `${libelle} : ${libelleAbsent}`
            : `${libelle} : ${affichage ?? formaterNombre(valeur)}, soit ${Math.round(part)} % de l'échelle${partRepere !== null ? `, repère à ${Math.round(partRepere)} %` : ''}`
        }
      >
        <i className={classes(styles.segment, ton !== 'bleu' && styles[ton])} style={{ width: `${part}%` }} />
        {partRepere !== null ? <i className={styles.repere} style={{ left: `${partRepere}%` }} /> : null}
      </div>
    </div>
  );
}

export interface ColonneFlux {
  libelle: string;
  valeur: number | null;
}

export interface ProprietesFluxProgression {
  colonnes: ColonneFlux[];
  titre?: string;
  className?: string;
}

/** Flux de progression : colonnes à hauteur proportionnelle, libellés des statuts usager. */
export function FluxProgression({ colonnes, titre = 'Répartition par statut', className }: ProprietesFluxProgression) {
  const max = Math.max(1, ...colonnes.map((c) => c.valeur ?? 0));
  return (
    <figure className={className} style={{ margin: 0 }}>
      <div className={styles.flux} style={{ '--colonnes': colonnes.length } as CSSProperties} aria-hidden="true">
        {colonnes.map((c) => (
          <div key={c.libelle} className={styles.colonne}>
            <span className={styles.valeurFlux}>{c.valeur === null ? '-' : formaterNombre(c.valeur)}</span>
            <span className={styles.baton} style={{ height: `${((c.valeur ?? 0) / max) * 110}px` }} />
            <span className={styles.libelleFlux}>{c.libelle}</span>
          </div>
        ))}
      </div>
      <table className="cc-masque">
        <caption>{titre}</caption>
        <tbody>
          {colonnes.map((c) => (
            <tr key={c.libelle}>
              <th scope="row">{c.libelle}</th>
              <td>{c.valeur === null ? 'non renseigné' : formaterNombre(c.valeur)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </figure>
  );
}

export interface EvenementSemaine {
  id: string;
  titre: ReactNode;
  /** Type, porté par la bordure gauche et repris dans le texte accessible. */
  type?: 'conseil' | 'echeance' | 'retard' | 'autre';
  libelleType?: string;
}

export interface JourSemaine {
  date: string;
  libelle: string;
  evenements: EvenementSemaine[];
}

export interface ProprietesSemaine {
  jours: JourSemaine[];
  aujourdhui?: string;
  libelle?: string;
  vide?: string;
}

export function Semaine({ jours, aujourdhui, libelle = 'Semaine', vide = 'Rien de prévu' }: ProprietesSemaine) {
  return (
    <ol className={styles.semaine} style={{ '--jours': jours.length } as CSSProperties} aria-label={libelle}>
      {jours.map((j) => (
        <li key={j.date} className={classes(styles.jour, j.date === aujourdhui && styles.aujourdhui)} aria-current={j.date === aujourdhui ? 'date' : undefined}>
          <h4>
            {j.libelle}
            {j.date === aujourdhui ? <span className="cc-masque"> (aujourd&apos;hui)</span> : null}
          </h4>
          {j.evenements.length === 0 ? (
            <span className={styles.na}>{vide}</span>
          ) : (
            <ul className={styles.evenements}>
              {j.evenements.map((e) => (
                <li key={e.id} className={styles.evenement} data-type={e.type ?? 'autre'}>
                  {e.libelleType ? <span className="cc-masque">{e.libelleType} : </span> : null}
                  {e.titre}
                </li>
              ))}
            </ul>
          )}
        </li>
      ))}
    </ol>
  );
}
